/**
 * useMissions Hook - Fetches missions from LangGraph Store
 * v13 Section 4.5 - Mission Card Specifications
 *
 * Sprint 11a Update: Removed mock data seeding, integrated with Ikigai scoring.
 * Missions are now generated ONLY by agents (TriggerContext) and sorted
 * by well-being alignment (IkigaiContext).
 *
 * Data Flow:
 * 1. DataSourceContext syncs data → IAB classifications stored
 * 2. TriggerContext watches store → triggers agents on new classifications
 * 3. Agents generate MissionCards → stored in ownyou.missions namespace
 * 4. This hook fetches missions → sorted by Ikigai well-being score
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { NS } from '@ownyou/shared-types';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import { useAwardPoints } from './useRewards';
import {
  rankMissions as ikigaiRankMissions,
  type RankableMission,
  type DimensionScores,
} from '@ownyou/ikigai';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../lib/mission-spine/instrumentation';
import {
  getMissionCardLifecycleState,
  type CardFirstMissionCard,
  type LegacyMissionStatus,
  type MissionState,
} from '../lib/missions/lifecycle';
import {
  applyMissionFeedbackRanking,
  filterExactDislikedMissions,
  listMissionReactions,
  mergeLegacyCardFeedbackReactions,
} from '../lib/missions/reactions';
import { useMissionTransition } from './useMissionTransition';

// Sprint 17c: Quality gate — resilient import for test environments
let filterByQualityFn: ((missions: unknown[]) => { passed: unknown[]; filtered: unknown[]; scores: Map<string, unknown> }) | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
  const qualityModule = require('@ownyou/ikigai');
  if (qualityModule.filterByQuality) {
    filterByQualityFn = qualityModule.filterByQuality;
  }
} catch {
  // Quality gate not available, missions pass through unfiltered
}

// Optional: Import Ikigai context for scoring (if available)
// This is wrapped in try/catch to allow the hook to work standalone
let useIkigaiScoring: (() => {
  sort: (m: Mission[]) => Mission[];
  hasProfile: boolean;
  getDimensionScores?: () => DimensionScores | null;
}) | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
  const ikigai = require('../contexts/IkigaiContext');
  useIkigaiScoring = ikigai.useMissionScoring;
} catch {
  // IkigaiContext not available, scoring disabled
}

/** Normalize agent output types to UI card types (Sprint 17d Package 6) */
type MissionCardType = 'shopping' | 'savings' | 'consumables' | 'content' | 'travel' | 'entertainment' | 'food' | 'people' | 'health';

export const AGENT_TO_UI_TYPE_MAP: Record<string, MissionCardType> = {
  shopping: 'shopping',
  savings: 'savings',
  travel: 'travel',
  content: 'content',
  food: 'food',
  restaurant: 'food',
  entertainment: 'entertainment',
  events: 'entertainment',
  health: 'health',
  diagnostic: 'health',
  people: 'people',
  consumables: 'consumables',
};

export interface Mission {
  id: string;
  type: MissionCardType;
  title: string;
  description?: string;
  imageUrl?: string;
  brandName?: string;
  brandLogoUrl?: string;
  price?: number;
  originalPrice?: number;
  savings?: number;
  currency?: string;
  actionUrl?: string;
  actionLabel?: string;
  reason?: string;
  tags?: string[];
  evidenceChain?: string[];
  relatedMissionIds?: string[];
  entitySource?: string;
  entityCategory?: string;
  entityConfidence?: number;
  entityType?: string;
  feedbackState?: 'meh' | 'like' | 'love';
  status?: LegacyMissionStatus;
  state?: MissionState;
  snoozedUntil?: number;
  createdAt: Date;
  priority: number;
  /** Ikigai alignment boost from ranking (1.0 = none, 1.5 = 2 dims, 2.0 = 3+ dims) */
  alignmentBoost?: number;
  /** Which Ikigai dimensions this mission aligns with */
  alignedDimensions?: string[];
  /** Whether this mission targets the user's weakest Ikigai dimension */
  isGrowthMission?: boolean;
  /** Prediction text generated by agent worker — "We think you'd love..." */
  prediction?: string;
}

export function mapStoreMissionToMission(item: Mission): Mission {
  const raw = item as unknown as Record<string, unknown>;
  let description = item.description || (raw.summary as string) || '';
  if (description.includes('found 0 deals')) {
    const entityMatch = item.title?.match(/:\s*(.+)$/);
    const entity = entityMatch?.[1] ?? 'this';
    description = `Based on your browsing, you might be interested in ${entity}. We'll notify you when deals become available.`;
  }

  const entitySource = raw.entitySource as string | undefined;
  const reason = item.reason
    || (entitySource ? `Based on your love of ${entitySource}` : undefined);

  return {
    ...item,
    type: AGENT_TO_UI_TYPE_MAP[raw.type as string] ?? 'content',
    description,
    reason,
    tags: item.tags || (raw.ikigaiDimensions as string[]) || [],
    evidenceChain: item.evidenceChain || (raw.evidenceRefs as string[]) || [],
    alignmentBoost: item.alignmentBoost ?? (raw.ikigaiAlignmentBoost as number) ?? undefined,
    actionLabel: item.actionLabel || ((raw.primaryAction as Record<string, unknown>)?.label as string) || undefined,
    actionUrl: item.actionUrl || undefined,
    priority: item.priority ?? 50,
    createdAt: new Date(item.createdAt),
  } satisfies Mission;
}

export function filterHardVisibleMissions(missions: Mission[], now = Date.now()): Mission[] {
  return missions.filter(m => {
    if (!m.title || m.title.trim() === '') return false;
    if (/:\s*$/.test(m.title)) return false;
    if (m.description?.includes('found 0 deals')) return false;
    const raw = m as unknown as Record<string, unknown>;
    const state = getMissionCardLifecycleState(raw as unknown as CardFirstMissionCard, now);
    if (state === 'dismissed' || state === 'completed' || state === 'expired') return false;
    if (state === 'snoozed') {
      const snoozedUntil = raw.snoozedUntil as number | undefined;
      if (snoozedUntil && snoozedUntil > now) return false;
    }
    return true;
  });
}

export function dedupeMissionsByTitle(missions: Mission[]): Mission[] {
  const bestByTitle = new Map<string, Mission>();
  for (const mission of missions) {
    const key = mission.title.trim();
    const existing = bestByTitle.get(key);
    if (!existing || mission.createdAt > existing.createdAt) {
      bestByTitle.set(key, mission);
    }
  }
  return Array.from(bestByTitle.values());
}

export function prepareVisibleMissionsFromStore(items: Mission[], now = Date.now()): Mission[] {
  return dedupeMissionsByTitle(filterHardVisibleMissions(items.map(mapStoreMissionToMission), now));
}

interface MissionsQueryResult {
  missions: Mission[];
  allMissions: Mission[];
  rawMissionCount: number;
}

// REMOVED: Sample missions seeding - Sprint 11a
// Missions are now ONLY generated by agents through the TriggerContext.
// Empty state is handled in the UI with an onboarding flow.

// Filter logic removed — filtering is handled by MissionFeed's CATEGORY_TYPE_MAP.

/**
 * useMissions - Fetches and manages mission cards
 *
 * Sprint 11a: Missions are now ONLY generated by agents (no seeding).
 * - Fetches from LangGraph Store (ownyou.missions namespace)
 * - Applies Ikigai-based well-being scoring for sorting
 * - Returns empty array for new users (triggers onboarding UI)
 */
export function useMissions() {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const queryClient = useQueryClient();

  const userId = wallet?.address ?? 'anonymous';

  // Get Ikigai scoring if available (module-level constant, safe to call unconditionally)
  let ikigaiScoring: ReturnType<NonNullable<typeof useIkigaiScoring>> | null = null;
  try {
    ikigaiScoring = useIkigaiScoring ? useIkigaiScoring() : null;
  } catch {
    // IkigaiContext not mounted
  }

  // Query missions from store
  const query = useQuery({
    queryKey: ['missions', userId, isReady, ikigaiScoring?.hasProfile ?? false],
    queryFn: async (): Promise<MissionsQueryResult> => {
      // If not authenticated or store not ready, return empty
      if (!store || !isReady) {
        return { missions: [], allMissions: [], rawMissionCount: 0 };
      }

      try {
        const namespace = NS.missionCards(userId);
        const result = await store.list<Mission>(namespace, { limit: 100, offset: 0 });
        const reactionNamespace = NS.missionFeedback(userId);
        const listedReactions = await listMissionReactions(store, userId);
        const rawMissionCount = result.items.length;
        console.log(`[useMissions] Loaded ${rawMissionCount} raw missions from Store`);

        const allMissions = result.items.map(mapStoreMissionToMission);
        const missionReactions = mergeLegacyCardFeedbackReactions(
          listedReactions,
          allMissions
        );
        let missions = allMissions;

        console.log(`[useMissions] After mapping: ${missions.length} missions`);

        missions = filterHardVisibleMissions(missions);

        console.log(`[useMissions] After hard filter: ${missions.length} missions`);

        const exactDislikedFilter = filterExactDislikedMissions(
          missions,
          missionReactions
        );
        missions = exactDislikedFilter.missions;

        console.log(`[useMissions] After exact feedback filter: ${missions.length} missions`);

        missions = dedupeMissionsByTitle(missions);
        console.log(`[useMissions] After dedup: ${missions.length} missions (removed ${missions.length < (result.items?.length ?? 0) ? 'some' : 'none'})`);

        // Sprint 17c: Quality gate scoring
        if (filterByQualityFn) {
          const qualityInputs = missions.map(m => ({
            ...m,
            entityConfidence: (m as any).entityConfidence ?? undefined,
            signalCount: m.evidenceChain?.length,
          }));
          try {
            const { passed, scores } = filterByQualityFn(qualityInputs);
            for (const [title, score] of scores) {
              const s = score as Record<string, number>;
              console.log(`[QualityGate] "${String(title).slice(0, 40)}..." → ${s.total} (spec=${s.specificity} act=${s.actionability} evi=${s.evidence} fresh=${s.freshness})`);
            }
            missions = passed as unknown as Mission[];
          } catch (err) {
            console.warn('[QualityGate] Scoring failed, passing all missions through:', err);
          }
        }

        console.log(`[useMissions] After quality gates: ${missions.length} missions passed`);

        // Sprint 17f-1 Task 3B: Diversity sort (no cards dropped)
        const { sortForDiversity } = await import('./portfolio-optimizer');
        missions = sortForDiversity(missions as any) as unknown as Mission[];
        console.log(`[useMissions] After diversity sort: ${missions.length} missions`);

        // Sort by Ikigai dimension ranking if profile available, otherwise by priority
        if (ikigaiScoring?.hasProfile && ikigaiScoring.getDimensionScores) {
          const scores = ikigaiScoring.getDimensionScores();
          if (scores) {
            // Convert missions to RankableMission format for ranking
            const rankable: RankableMission[] = missions.map(m => ({
              id: m.id,
              title: m.title,
              tags: m.tags ?? [],
              baseScore: 100 - m.priority, // Lower priority number = higher base score
            }));

            const ranked = ikigaiRankMissions(rankable, scores);
            // Re-order missions array to match ranked order and attach ranking data
            const rankingMap = new Map(ranked.map((r, i) => [r.id, { index: i, ranking: r.ranking }]));
            missions.sort((a, b) => (rankingMap.get(a.id)?.index ?? 999) - (rankingMap.get(b.id)?.index ?? 999));
            // Attach alignment metadata to each mission
            for (const m of missions) {
              const data = rankingMap.get(m.id);
              if (data) {
                m.alignmentBoost = data.ranking.alignmentBoost;
                m.alignedDimensions = data.ranking.alignedDimensions;
                m.isGrowthMission = data.ranking.growthBoost > 1.0;
              }
            }
          } else {
            missions.sort((a, b) => a.priority - b.priority);
          }
        } else {
          missions.sort((a, b) => a.priority - b.priority);
        }

        const feedbackRanking = applyMissionFeedbackRanking(
          missions,
          allMissions,
          missionReactions
        );
        missions = feedbackRanking.missions;

        recordMissionSpineMarker({
          runId: createMissionSpineRunId('feedback-ranking'),
          hop: 'feedback_ranking_result',
          status: missionReactions.length > 0 ? 'success' : 'skipped',
          namespace: reactionNamespace[0],
          reasonCode: missionReactions.length > 0 ? 'feedback-policy-applied' : 'no-reactions',
          counts: {
            reactionCount: missionReactions.length,
            exactHiddenCount: exactDislikedFilter.hiddenCount,
            boostedCount: feedbackRanking.summary.boostedCount,
            downrankedCount: feedbackRanking.summary.downrankedCount,
            visibleCount: missions.length,
          },
        });

        recordMissionSpineMarker({
          runId: createMissionSpineRunId('use-missions'),
          hop: 'use_missions_result',
          status: missions.length > 0 ? 'success' : 'skipped',
          namespace: namespace[0],
          reasonCode: missions.length > 0 ? 'missions-returned' : 'no-visible-missions',
          counts: {
            rawCount: rawMissionCount,
            postFilterCount: missions.length,
          },
        });

        return { missions, allMissions, rawMissionCount };
      } catch (error) {
        console.error('Failed to fetch missions:', error);
        throw error;
      }
    },
    enabled: isReady,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Refresh missions when new ones are generated
  const refreshMissions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['missions'] });
  }, [queryClient]);

  // Sprint 17e: Refresh state (read once on mount, L3)
  const lastRefreshAt = useMemo(() => {
    try {
      const ts = localStorage.getItem('ownyou_last_agent_refresh');
      return ts ? new Date(parseInt(ts, 10)) : null;
    } catch { return null; }
  }, []);

  return {
    missions: query.data?.missions ?? [],
    allMissions: query.data?.allMissions ?? [],
    isLoading: query.isLoading || !isReady,
    error: query.error as Error | null,
    refetch: query.refetch,
    refreshMissions,
    /** Whether missions are from store (true) or pending (false) */
    isStoreReady: isReady,
    /** Whether Ikigai scoring is being applied */
    isIkigaiSorted: ikigaiScoring?.hasProfile ?? false,
    /** Whether user has any missions (false = show onboarding) */
    hasMissions: (query.data?.allMissions.length ?? 0) > 0,
    /** Sprint 17e: When missions were last refreshed */
    lastRefreshAt,
  };
}

/**
 * Hook to update mission status (snooze, dismiss, complete)
 * Sprint 11b Bugfix 8: Wire mission card actions
 */
export function useUpdateMission() {
  const { awardPoints } = useAwardPoints();
  const { transitionMissionState, completeMissionState, isReady } = useMissionTransition();

  const acceptMission = useCallback(async (missionId: string) => {
    return (await transitionMissionState(missionId, 'accepted')).card;
  }, [transitionMissionState]);

  const startMission = useCallback(async (missionId: string) => {
    return (await transitionMissionState(missionId, 'in_progress')).card;
  }, [transitionMissionState]);

  const snoozeMission = useCallback(async (missionId: string, hours: number = 24) => {
    const snoozedUntil = Date.now() + hours * 60 * 60 * 1000;
    return (await transitionMissionState(missionId, 'snoozed', { reason: 'custom_hours', snoozedUntil })).card;
  }, [transitionMissionState]);

  const dismissMission = useCallback(async (missionId: string) => {
    return (await transitionMissionState(missionId, 'dismissed')).card;
  }, [transitionMissionState]);

  const completeMission = useCallback(async (missionId: string) => {
    const updated = (await completeMissionState(missionId)).card;
    // Sprint 17d Package 8: Award points on completion
    if (updated && awardPoints) {
      const raw = updated as unknown as Record<string, unknown>;
      await awardPoints(
        missionId,
        (raw.title as string) || 'Mission',
        (raw.type as string) || 'content',
      ).catch(err => {
        console.warn('[useUpdateMission] Completion reward failed after lifecycle write:', err);
      });
    }
    return updated;
  }, [completeMissionState, awardPoints]);

  return {
    acceptMission,
    startMission,
    snoozeMission,
    dismissMission,
    completeMission,
    isReady,
  };
}

/**
 * Hook to add a new mission to the store
 */
export function useAddMission() {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const queryClient = useQueryClient();

  const addMission = useCallback(async (mission: Omit<Mission, 'id' | 'createdAt'>) => {
    if (!store || !isReady || !wallet) {
      throw new Error('Store not ready or not authenticated');
    }

    const newMission: Mission = {
      ...mission,
      id: `mission-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      createdAt: new Date(),
    };

    const namespace = NS.missionCards(wallet.address);
    await store.put(namespace, newMission.id, {
      ...newMission,
      createdAt: newMission.createdAt.toISOString(),
    });

    // Invalidate to refetch
    queryClient.invalidateQueries({ queryKey: ['missions'] });

    return newMission;
  }, [store, isReady, wallet, queryClient]);

  return { addMission, isReady };
}
